import React, { useEffect, useState, useRef, useCallback } from 'react';
import './AlertModal.scss';
import PropTypes from 'prop-types';
import { subscribe } from './alertModalService';

export function AlertModalItem({ modal, onRemove }) {
   const modalRef = useRef(null);

   const {
      id,
      title,
      visible = true,
      onRequestClose,
      noButton,
      noAction,
      yesButton,
      yesAction,
      optionalButton,
      optionalAction,
      description,
      icon,
      customClassName,
      closeIcon,
      closeIconAction,
   } = modal;

   const handleClose = useCallback(() => {
      if (onRequestClose) {
         onRequestClose();
      }
      onRemove(id);
   }, [id, onRequestClose, onRemove]);

   const handleNoClick = useCallback(() => {
      noAction?.();
      handleClose();
   }, [noAction, handleClose]);

   const handleOptionalClick = useCallback(() => {
      optionalAction?.();
   }, [optionalAction]);

   const handleYesClick = useCallback(() => {
      yesAction?.();
      handleClose();
   }, [yesAction, handleClose]);

   useEffect(() => {
      const handleClickOutside = event => {
         if (modalRef.current && !modalRef.current.contains(event.target)) {
            handleClose();
         }
      };

      if (visible) {
         document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
         document.removeEventListener('mousedown', handleClickOutside);
      };
   }, [visible, handleClose]);

   if (!visible) return null;

   return (
      <div className="alert-modal-backdrop ct-flexCenter" style={{ zIndex: 1000 + (modal.zIndex || 0) }}>
         <div
            className={`alert-modal-content ct-flex ct-fcol ct-aiCenter ct-jcCenter ct-gap24 ct-relative ${customClassName || ''}`}
            ref={modalRef}
         >
            {closeIcon && (
               <button
                  type="button"
                  className="alert-modal-close-icon ct-flexCenter"
                  onClick={closeIconAction || handleClose}
                  aria-label="Close"
               >
                  {closeIcon}
               </button>
            )}
            <div className="ct-flex ct-aiCenter ct-jcCenter ct-fcol ct-gap8">
               {icon && <div className="alert-modal-icon ct-flexCenter">{icon}</div>}
               {title && <h3 className="alert-modal-title ct-text-center">{title}</h3>}
            </div>
            {description && (
               <div className="alert-modal-description ct-text-center">
                  {typeof description === 'string' ? (
                     <p dangerouslySetInnerHTML={{ __html: description }} />
                  ) : (
                     description
                  )}
               </div>
            )}
            <div className="alert-modal-buttons-container ct-wFull">
               <div className="alert-modal-buttons ct-flex ct-aiCenter ct-jcCenter ct-gap16">
                  {noButton && (
                     <button className="alert-button ct-flexCenter" onClick={handleNoClick}>
                        {noButton}
                     </button>
                  )}
                  {optionalButton && (
                     <button className="alert-button ct-flexCenter " onClick={handleOptionalClick}>
                        {optionalButton}
                     </button>
                  )}
                  {yesButton && (
                     <button className="alert-button alert-button-primary ct-flexCenter" onClick={handleYesClick}>
                        {yesButton}
                     </button>
                  )}
               </div>
            </div>
         </div>
      </div>
   );
}

const addModal = action => prev => [...prev, { ...action, id: action.id || Date.now() }];
const filterModal = id => prev => prev.filter(m => m.id !== id);

function AlertModalRoot() {
   const [modals, setModals] = useState([]);

   const handleRemove = useCallback(id => {
      setModals(filterModal(id));
   }, []);

   useEffect(() => {
      const handleAction = action => {
         if (action.type === 'SHOW') {
            setModals(addModal(action));
         } else if (action.type === 'HIDE') {
            setModals(filterModal(action.id));
         } else if (action.type === 'CLEAR') {
            setModals([]);
         }
      };
      return subscribe(handleAction);
   }, []);

   if (!modals.length) return null;

   return (
      <>
         {modals.map(modal => (
            <AlertModalItem key={modal.id} modal={modal} onRemove={handleRemove} />
         ))}
      </>
   );
}

AlertModalItem.propTypes = {
   modal: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.node,
      visible: PropTypes.bool,
      onRequestClose: PropTypes.func,
      noButton: PropTypes.string,
      noAction: PropTypes.func,
      yesButton: PropTypes.string,
      yesAction: PropTypes.func,
      optionalButton: PropTypes.string,
      optionalAction: PropTypes.func,
      description: PropTypes.node,
      icon: PropTypes.node,
      customClassName: PropTypes.string,
      zIndex: PropTypes.number,
      closeIcon: PropTypes.node,
      closeIconAction: PropTypes.func,
   }).isRequired,
   onRemove: PropTypes.func.isRequired,
};

export default AlertModalRoot;
